import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services/api";
import toast from "react-hot-toast";
import { Plus, Building2, Trash2, Pencil, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const empty = {
  organization_name: "",
  description: "",
  business_owner: "",
  email: "",
  phone: "",
  gst_number: "",
  address: "",
  city: "",
  state: "",
  state_code: "",
  country: "India",
  pincode: "",
  logo: "",
  bank_name: "",
  account_number: "",
  ifsc: "",
  upi_id: "",
  website: "",
  invoice_prefix: "INV-",
  credit_period: 30,
  default_notes: "",
  default_terms: "",
  signature: "",
};

const readFile = (f) =>
  new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.readAsDataURL(f);
  });

export default function OrganizationsPage() {
  const qc = useQueryClient();
  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ["orgs"],
    queryFn: async () => (await api.get("/organizations")).data,
  });

  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [savedForm, setSavedForm] = useState(null); // snapshot at edit-open time

  // True when editing and the form differs from the saved snapshot
  const isDirty = editId
    ? JSON.stringify(form) !== JSON.stringify(savedForm)
    : true; // new org — always allow save

  const save = useMutation({
    mutationFn: async () => {
      if (editId) return (await api.put(`/organizations/${editId}`, form)).data;
      return (await api.post("/organizations", form)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs"] });
      toast.success(editId ? "Organization updated" : "Organization created");
      setOpenForm(false);
      setForm(empty);
      setEditId(null);
      setSavedForm(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/organizations/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs"] });
      toast.success("Deleted");
    },
  });

  const startEdit = (o) => {
    setEditId(o.id);
    const initial = { ...empty, ...o };
    setForm(initial);
    setSavedForm(initial); // capture snapshot for dirty check
    setOpenForm(true);
  };

  const uploadLogo = async (e, field) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const dataUrl = await readFile(f);
    setForm((prev) => ({ ...prev, [field]: dataUrl }));
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-2">Business Profiles</div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter">Organizations.</h1>
        </div>
        <button
          data-testid="new-organization-button"
          onClick={() => {
            setEditId(null);
            setForm(empty);
            setOpenForm(true);
          }}
          className="inline-flex items-center gap-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-4 py-2.5 sm:px-5 sm:py-3 rounded-sm hover:bg-[#002FA7] hover:text-white transition-colors font-semibold self-start md:self-auto text-sm sm:text-base"
        >
          <Plus className="w-4 h-4" /> New Organization
        </button>
      </div>

      {isLoading ? (
        <div className="text-slate-500">Loading…</div>
      ) : orgs.length === 0 ? (
        <div className="border border-dashed border-slate-300 dark:border-slate-700 p-20 text-center rounded-sm" data-testid="orgs-empty-state">
          <Building2 className="w-8 h-8 mx-auto text-slate-400 mb-4" />
          <div className="text-lg mb-2">No organizations yet.</div>
          <div className="text-slate-500">Create your first business profile to start invoicing.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {orgs.map((o, i) => (
            <motion.div
              key={o.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="border border-slate-200 dark:border-slate-800 rounded-sm p-6 bg-white dark:bg-[#121214] hover:border-slate-900 dark:hover:border-white transition-colors"
              data-testid={`org-card-${o.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-sm bg-slate-100 dark:bg-slate-900 flex items-center justify-center overflow-hidden">
                  {o.logo ? <img src={o.logo} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-5 h-5" />}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => startEdit(o)} className="p-2 hover:text-[#002FA7]" data-testid={`edit-org-${o.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => window.confirm("Delete organization and all its invoices?") && del.mutate(o.id)}
                    className="p-2 hover:text-red-600"
                    data-testid={`delete-org-${o.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="text-lg font-semibold mb-1">{o.organization_name}</div>
              <div className="text-sm text-slate-500 mb-4">{o.business_owner || "—"}</div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="uppercase tracking-[0.2em] text-slate-500">GST</div>
                  <div>{o.gst_number || "—"}</div>
                </div>
                <div>
                  <div className="uppercase tracking-[0.2em] text-slate-500">Prefix</div>
                  <div>{o.invoice_prefix}</div>
                </div>
                <div className="col-span-2">
                  <div className="uppercase tracking-[0.2em] text-slate-500">Location</div>
                  <div>{[o.city, o.state, o.country].filter(Boolean).join(", ") || "—"}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {openForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setOpenForm(false)}
          >
            <motion.div
              initial={{ y: 50 }}
              animate={{ y: 0 }}
              exit={{ y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-[#121214] rounded-sm w-full max-w-3xl max-h-[90vh] overflow-auto"
            >
              <div className="p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-800 sticky top-0 bg-white dark:bg-[#121214]">
                <h2 className="text-2xl font-extrabold tracking-tighter">
                  {editId ? "Edit organization" : "New organization"}
                </h2>
                <button onClick={() => setOpenForm(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  ["organization_name", "Business name *"],
                  ["description", "Description"],
                  ["business_owner", "Owner name"],
                  ["email", "Email"],
                  ["phone", "Phone"],
                  ["gst_number", "GST number"],
                  ["website", "Website"],
                  ["address", "Address"],
                  ["city", "City"],
                  ["state", "State"],
                  ["state_code", "State Code"],
                  ["country", "Country"],
                  ["pincode", "Pincode"],
                  ["invoice_prefix", "Invoice prefix"],
                  ["credit_period", "Credit period (days)"],
                  ["bank_name", "Bank name"],
                  ["account_number", "Account number"],
                  ["ifsc", "IFSC"],
                  ["upi_id", "UPI ID"],
                ].map(([k, label]) => (
                  <label key={k} className={k === "address" || k === "description" ? "md:col-span-2" : ""}>
                    <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">{label}</span>
                    {k === "description" ? (
                      <textarea
                        data-testid={`org-field-${k}`}
                        value={form[k] || ""}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        rows={2}
                        className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                      />
                    ) : k === "credit_period" ? (
                      <input
                        type="number"
                        data-testid={`org-field-${k}`}
                        value={form[k] || ""}
                        onChange={(e) => setForm({ ...form, [k]: +e.target.value })}
                        className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                      />
                    ) : (
                      <input
                        data-testid={`org-field-${k}`}
                        value={form[k] || ""}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                      />
                    )}
                  </label>
                ))}
                <label className="md:col-span-2">
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Default notes</span>
                  <textarea
                    data-testid="org-field-default_notes"
                    value={form.default_notes}
                    onChange={(e) => setForm({ ...form, default_notes: e.target.value })}
                    rows={2}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                  />
                </label>
                <label className="md:col-span-2">
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Default terms</span>
                  <textarea
                    data-testid="org-field-default_terms"
                    value={form.default_terms}
                    onChange={(e) => setForm({ ...form, default_terms: e.target.value })}
                    rows={2}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                  />
                </label>

                <label>
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Logo</span>
                  <input type="file" accept="image/*" onChange={(e) => uploadLogo(e, "logo")} className="mt-1 block w-full text-sm" data-testid="org-logo-upload" />
                  {form.logo && (
                    <div className="mt-2 flex items-start gap-3">
                      <img src={form.logo} alt="" className="h-16 border border-slate-200 dark:border-slate-800" />
                      <button
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, logo: "" }))}
                        className="text-xs text-red-600 hover:underline"
                        data-testid="org-logo-remove"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </label>
                <label>
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Signature</span>
                  <input type="file" accept="image/*" onChange={(e) => uploadLogo(e, "signature")} className="mt-1 block w-full text-sm" data-testid="org-signature-upload" />
                  {form.signature && (
                    <div className="mt-2 flex items-start gap-3">
                      <img src={form.signature} alt="" className="h-16 border border-slate-200 dark:border-slate-800" />
                      <button
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, signature: "" }))}
                        className="text-xs text-red-600 hover:underline"
                        data-testid="org-signature-remove"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </label>
              </div>
              <div className="p-6 flex items-center justify-end gap-3 border-t border-slate-200 dark:border-slate-800 sticky bottom-0 bg-white dark:bg-[#121214]">
                <button onClick={() => { setOpenForm(false); setSavedForm(null); }} className="px-5 py-2.5 border border-slate-300 dark:border-slate-700 rounded-sm">
                  Cancel
                </button>
                <button
                  data-testid="save-organization-button"
                  disabled={!form.organization_name || save.isPending || !isDirty}
                  onClick={() => save.mutate()}
                  className="px-5 py-2.5 bg-[#002FA7] text-white rounded-sm hover:bg-[#001F73] disabled:opacity-50"
                >
                  {save.isPending ? "Saving…" : "Save"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
